import * as React from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import { Button } from '../../../ui/button';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandSeparator,
} from '../../../ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../../../ui/popover';
import { SearchProvider } from '../../types';

interface DocumentComboboxProps {
    options: string[];
    fixedOptions?: string[];
    sourceLabel?: string;
    value?: string;
    onChange?: (value: string, option?: any) => void;
    /** Fires only on explicit commits: option selection, Enter, or
     *  popover close with non-empty text.  Useful when the parent
     *  should not react to every keystroke (e.g. repeater combo-add). */
    onCommit?: (value: string) => void;
    placeholder?: string;
    className?: string;
    showSearch?: boolean;
    onKeyDown?: (e: React.KeyboardEvent, option?: any) => void;
    searchProvider?: SearchProvider;
    selectOnlyOnEnter?: boolean;
}

export function DocumentCombobox({
    options = [],
    fixedOptions = [],
    sourceLabel,
    value,
    onChange,
    onCommit,
    placeholder = 'Select option...',
    className,
    showSearch = false,
    onKeyDown,
    searchProvider,
    selectOnlyOnEnter = false,
}: DocumentComboboxProps) {
    const [open, setOpen] = React.useState(false);
    const [inputValue, setInputValue] = React.useState(value || '');
    const [searchOptions, setSearchOptions] = React.useState<string[]>([]);
    const [rawOptions, setRawOptions] = React.useState<any[]>([]);
    const [loading, setLoading] = React.useState(false);
    const commandRef = React.useRef<HTMLDivElement>(null);
    // Tracks whether the popover close was caused by an explicit action
    // (option select or Enter) that already fires onCommit itself, so
    // the onOpenChange handler can skip the duplicate call.
    const commitHandledRef = React.useRef(false);

    React.useEffect(() => {
        if (value !== undefined) {
            setInputValue(value);
        }
    }, [value]);

    React.useEffect(() => {
        if (!showSearch) {
            setSearchOptions(options);
        }
    }, [options, showSearch]);

    // Filter fixed options client-side when showSearch is active
    const filteredFixedOptions = React.useMemo(() => {
        if (!fixedOptions.length) return [];
        if (!showSearch) return fixedOptions; // cmdk handles filtering
        if (!inputValue) return fixedOptions;
        const lower = inputValue.toLowerCase();
        return fixedOptions.filter((o) => o.toLowerCase().includes(lower));
    }, [fixedOptions, inputValue, showSearch]);

    // Deduplicate: remove fixed options that already appear in source/search results
    const deduplicatedFixedOptions = React.useMemo(() => {
        if (!filteredFixedOptions.length) return [];
        const searchSet = new Set(
            searchOptions.map((o) => o.toLowerCase())
        );
        return filteredFixedOptions.filter(
            (o) => !searchSet.has(o.toLowerCase())
        );
    }, [filteredFixedOptions, searchOptions]);

    const hasSourceOptions = searchOptions.length > 0;
    const hasFixedOptions = deduplicatedFixedOptions.length > 0;

    const getHighlightedOptionValue = React.useCallback(() => {
        const selectedItem = commandRef.current?.querySelector<HTMLElement>(
            '[data-option-value][data-selected="true"]'
        );
        return selectedItem?.dataset.optionValue;
    }, []);

    const handleSelect = (currentValue: string) => {
        setInputValue(currentValue);
        const selectedOption = rawOptions.find(
            (o) =>
                (typeof o === 'string' ? o : o.term || o.name || o.label) ===
                currentValue
        );
        // Close popover first, then call onChange after a small delay
        // This prevents ResizeObserver loop errors from async operations during resize
        commitHandledRef.current = true;
        setOpen(false);
        setTimeout(() => {
            onChange?.(currentValue, selectedOption);
            onCommit?.(currentValue);
        }, 200);
    };

    const handleInputChange = async (search: string) => {
        setInputValue(search);
        onChange?.(search);

        if (showSearch && search.length > 1) {
            setLoading(true);
            try {
                const results = searchProvider
                    ? await searchProvider(search)
                    : [];

                setRawOptions(Array.isArray(results) ? results : []);
                const mappedResults = Array.isArray(results)
                    ? results.map((r: any) =>
                          typeof r === 'string'
                              ? r
                              : r.term || r.name || r.label
                      )
                    : [];

                setSearchOptions(mappedResults);
            } catch (error) {
                console.error('Autocomplete error', error);
            } finally {
                setLoading(false);
            }
        } else if (showSearch) {
            setSearchOptions([]);
        }
    };

    const renderItem = (option: string) => (
        <CommandItem
            key={option}
            value={option}
            onSelect={handleSelect}
            data-option-value={option}
        >
            <Check
                className={cn(
                    'mr-2 h-4 w-4',
                    inputValue === option ? 'opacity-100' : 'opacity-0'
                )}
            />
            {option}
        </CommandItem>
    );

    return (
        <Popover
            open={open}
            onOpenChange={(nextOpen) => {
                setOpen(nextOpen);
                // Popover closing (click outside / escape) with typed text
                // counts as a commit for combo-add style usage.  Skip if
                // the close was triggered by handleSelect or Enter which
                // already called onCommit directly.
                if (!nextOpen && inputValue.trim() && !commitHandledRef.current) {
                    onCommit?.(inputValue.trim());
                }
                commitHandledRef.current = false;
            }}
        >
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    className={cn(
                        'w-full justify-between font-normal',
                        !inputValue && 'text-muted-foreground',
                        className
                    )}
                    onKeyDown={(e) => {
                        if (selectOnlyOnEnter && e.key === 'Enter') {
                            return;
                        }
                        onKeyDown?.(e);
                    }}
                >
                    {inputValue || placeholder}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
                <Command ref={commandRef} shouldFilter={!showSearch}>
                    <CommandInput
                        placeholder={placeholder}
                        value={inputValue}
                        onValueChange={handleInputChange}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();

                                if (selectOnlyOnEnter) {
                                    const highlightedOptionValue =
                                        getHighlightedOptionValue();

                                    if (highlightedOptionValue) {
                                        handleSelect(highlightedOptionValue);
                                        return;
                                    }

                                    commitHandledRef.current = true;
                                    setOpen(false);
                                    if (inputValue.trim()) {
                                        onCommit?.(inputValue.trim());
                                    }
                                    return;
                                }

                                // Close first, then call callback after delay to prevent ResizeObserver errors
                                commitHandledRef.current = true;
                                setOpen(false);
                                setTimeout(() => {
                                    if (inputValue.trim()) {
                                        onCommit?.(inputValue.trim());
                                    }
                                    onKeyDown?.(e);
                                }, 200);
                                return;
                            }
                            onKeyDown?.(e);
                        }}
                    />
                    <CommandList>
                        <CommandEmpty>
                            {loading
                                ? 'Loading...'
                                : 'Type to add custom value.'}
                        </CommandEmpty>
                        {hasSourceOptions && (
                            <CommandGroup
                                heading={sourceLabel || 'Suggestions'}
                            >
                                {searchOptions.map(renderItem)}
                            </CommandGroup>
                        )}
                        {hasSourceOptions && hasFixedOptions && (
                            <CommandSeparator />
                        )}
                        {hasFixedOptions && (
                            <CommandGroup
                                heading={
                                    hasSourceOptions ? 'Options' : undefined
                                }
                            >
                                {deduplicatedFixedOptions.map(renderItem)}
                            </CommandGroup>
                        )}
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    );
}
